// components/tour/TourTooltip.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as NextIcon,
  ArrowBack as PrevIcon,
  SkipNext as SkipIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const TourTooltip = ({ 
  step, 
  currentStep, 
  totalSteps, 
  onNext, 
  onPrevious, 
  onSkip, 
  onClose,
  elementRect 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom' });

  const calculatePosition = () => {
    if (!elementRect) {
      // Emergency fallback: center of viewport
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      return {
        top: windowHeight / 2 - 100,
        left: (windowWidth - (isMobile ? 300 : 400)) / 2,
        placement: 'center'
      };
    }

    const tooltipWidth = isMobile ? Math.min(300, window.innerWidth - 24) : 400;
    const tooltipHeight = isMobile ? 180 : 220;
    
    // Fixed offsets based on screen size
    const verticalOffset = isMobile ? 16 : 20;
    const horizontalPadding = isMobile ? 12 : 16;

    // Convert absolute document coordinates to viewport coordinates
    const viewportElementTop = elementRect.top - window.pageYOffset;
    const viewportElementLeft = elementRect.left - window.pageXOffset;

    // Determine if this step should show tooltip above or below
    const shouldShowAbove = step?.position === 'top' || 
                           step?.id === 'learning-summary' || 
                           step?.id === 'games-chat';

    let top, left, placement;

    if (isMobile) {
      // Mobile: Fixed positioning with padding from edges
      top = shouldShowAbove 
        ? viewportElementTop - tooltipHeight - verticalOffset
        : viewportElementTop + elementRect.height + verticalOffset;
      
      left = horizontalPadding;
      placement = shouldShowAbove ? 'top' : 'bottom';
      
      // Keep tooltip in viewport for mobile
      if (top < 10) {
        top = 10;
        placement = 'mobile-safe';
      }
      if (top + tooltipHeight > window.innerHeight - 10) {
        top = window.innerHeight - tooltipHeight - 10;
        placement = 'mobile-safe';
      }
    } else {
      // Desktop: Center tooltip horizontally relative to element
      const elementCenterX = viewportElementLeft + elementRect.width / 2;
      
      top = shouldShowAbove 
        ? viewportElementTop - tooltipHeight - verticalOffset
        : viewportElementTop + elementRect.height + verticalOffset;
      
      // Center tooltip horizontally on element, but keep within viewport
      left = Math.max(
        horizontalPadding, 
        Math.min(
          elementCenterX - tooltipWidth / 2, 
          window.innerWidth - tooltipWidth - horizontalPadding
        )
      );
      
      placement = shouldShowAbove ? 'top' : 'bottom';
      
      // Viewport bounds checking for desktop
      if (top < 10) {
        top = 10;
        placement = 'safe';
      }
      if (top + tooltipHeight > window.innerHeight - 10) {
        top = window.innerHeight - tooltipHeight - 10;
        placement = 'safe';
      }
    }

    console.log(`Step ${step?.id}: shouldShowAbove=${shouldShowAbove}, placement=${placement}, top=${top}`);

    return { top, left, placement };
  };

  useEffect(() => {
    setPosition(calculatePosition());
  }, [elementRect, isMobile, step?.id, step?.position]);

  useEffect(() => {
    const handleUpdate = () => {
      setPosition(calculatePosition());
    };
    
    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [elementRect, isMobile, step?.id, step?.position]);

  if (!step) return null;

  const tooltipStyle = {
    position: 'fixed', // Fixed positioning relative to viewport
    top: position.top,
    left: position.left,
    width: isMobile ? 'calc(100vw - 24px)' : '400px',
    zIndex: 10001,
    ...(isMobile && {
      left: '12px',
      right: '12px',
      width: 'auto',
    })
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 5 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 5 }}
        transition={{ duration: 0.15 }}
        style={tooltipStyle}
      >
        <Paper
          elevation={8}
          sx={{
            p: isMobile ? 1.5 : 2.5,
            borderRadius: isMobile ? 2 : 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `2px solid ${theme.palette.primary.main}`,
            position: 'relative',
            maxHeight: isMobile ? '200px' : '320px',
            minHeight: isMobile ? '150px' : '200px',
            overflow: 'auto',
            maxWidth: isMobile ? 'calc(100vw - 24px)' : '420px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.1)',
            
            // Simple arrows based on placement - only show for clear top/bottom placement
            ...(!isMobile && position.placement === 'bottom' && {
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: `8px solid ${theme.palette.primary.main}`,
              }
            }),
            ...(!isMobile && position.placement === 'top' && {
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: `8px solid ${theme.palette.primary.main}`,
              }
            })
          }}
        >
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: isMobile ? 1 : 1.5
          }}>
            <Box sx={{ flex: 1 }}>
              <Chip
                label={`${currentStep + 1} of ${totalSteps}`}
                size="small"
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '0.65rem' : '0.75rem',
                  height: isMobile ? 20 : 24,
                  mb: isMobile ? 0.5 : 1
                }}
              />
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                fontWeight="700" 
                color={theme.palette.primary.main}
                sx={{ 
                  lineHeight: 1.2,
                  fontSize: isMobile ? '0.9rem' : '1.1rem'
                }}
              >
                {step.title}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ 
                color: theme.palette.grey[500],
                '&:hover': { bgcolor: theme.palette.grey[100] },
                width: isMobile ? 28 : 32,
                height: isMobile ? 28 : 32
              }}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>

          {/* Content */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: isMobile ? 1.5 : 2, 
              lineHeight: 1.4,
              fontSize: isMobile ? '0.8rem' : '0.9rem'
            }}
          >
            {step.content}
          </Typography>

          {/* Action Buttons */}
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onNext}
                endIcon={<NextIcon fontSize="small" />}
                size="small"
                sx={{
                  py: 0.8,
                  px: 2,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  minHeight: 36,
                  width: '30%',
                  minWidth: '120px',
                  alignSelf: 'center'
                }}
              >
                {currentStep === totalSteps - 1 ? 'Complete' : 'Next'}
              </Button>
              
              <Box sx={{ display: 'flex', gap: 0.5, width: '100%' }}>
                {currentStep > 0 && (
                  <Button
                    variant="outlined"
                    onClick={onPrevious}
                    startIcon={<PrevIcon fontSize="small" />}
                    size="small"
                    sx={{ 
                      flex: 1,
                      py: 0.6,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      minHeight: 32
                    }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  variant="text"
                  onClick={onSkip}
                  size="small"
                  sx={{ 
                    flex: 1,
                    py: 0.6,
                    color: theme.palette.grey[600],
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minHeight: 32
                  }}
                >
                  Skip
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {currentStep > 0 && (
                  <Button
                    variant="outlined"
                    onClick={onPrevious}
                    startIcon={<PrevIcon />}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    Previous
                  </Button>
                )}
                <Button
                  variant="text"
                  onClick={onSkip}
                  startIcon={<SkipIcon />}
                  sx={{ 
                    color: theme.palette.grey[600],
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Skip
                </Button>
              </Box>
              
              <Button
                variant="contained"
                color="primary"
                onClick={onNext}
                endIcon={<NextIcon />}
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3
                }}
              >
                {currentStep === totalSteps - 1 ? 'Complete' : 'Next'}
              </Button>
            </Box>
          )}
        </Paper>
      </motion.div>
    </AnimatePresence>
  );
};

export default TourTooltip;